/**
 * Funnel field type constants for signup flow.
 * Extracted to avoid circular dependencies between firebase.ts and screen files.
 */

export const STATIC_FIELDS = [
  'title',
  'image',
  'button',
  'titleWithSubtitle',
  'reviewsSlider',
  'loader',
] as const

export type StaticFieldType = (typeof STATIC_FIELDS)[number]

/**
 * Field types whose renderer in `FunnelScreen` draws its own "continue" button.
 *
 * `singleSelect` is absent deliberately — tapping an option submits the screen, so it needs no
 * button. `button` and `loader` are their own submit and are likewise not listed.
 */
export const CONTINUE_BUTTON_FIELDS = [
  'textField',
  'numberField',
  'emailField',
  'phoneField',
  'passwordField',
  'multiSelect',
  'checkboxField',
] as const

interface FunnelContentItem {
  type: string
}

/**
 * Whether the content item at `index` should draw the screen's "continue" button.
 *
 * ─── WHY THIS EXISTS (WA-ONBOARD-001) ────────────────────────────────────────────────────────
 * Each input renderer used to draw its own button unconditionally, which was fine while every
 * screen had exactly one input — all 28 of them did. Adding the WhatsApp opt-in gave one screen a
 * second interactive field, and the naive result puts the consent checkbox BELOW the primary
 * action: the merchant taps "continue" and never sees what they agreed to. For a consent control
 * that is not a cosmetic complaint, it is the difference between an informed opt-in and a
 * pre-ticked box with extra steps.
 *
 * The rule is "the LAST interactive field owns the button", rather than a `hideContinue` flag on
 * the phone field, because a flag would leave the phone screen with no button at all if someone
 * later removed the checkbox. This cannot produce that state: whichever interactive field ends up
 * last gets the button. Verified a no-op on every other screen by
 * `whatsappOnboardingConsent.test.js`, which asserts every screen still has exactly one way to
 * move forward.
 */
export const ownsContinueButton = (
  content: readonly FunnelContentItem[],
  index: number,
): boolean => {
  const fields: readonly string[] = CONTINUE_BUTTON_FIELDS
  if (!fields.includes(content[index]?.type)) return false
  return !content.slice(index + 1).some((item) => fields.includes(item.type))
}
